import { useMemo, useState, useRef } from "react";
import { useRoute, useLocation } from "wouter";
import { trpc } from "@/utils/trpc";
import { toast } from "sonner";
import { Loader2, Paperclip, Send, ChevronDown } from "lucide-react";
import { QA, RATING_META, BAND_META, useQaPerms, useOpenEvidence, QaCrumb } from "./qa-shared";

type Rating = "compliant" | "partially_compliant" | "non_compliant" | "not_applicable";
const RATINGS: Rating[] = ["compliant", "partially_compliant", "non_compliant", "not_applicable"];
const RISKS = ["high", "medium", "low"] as const;

interface AState {
  rating: Rating | null;
  evidenceReviewed: string;
  riskLevel: "high" | "medium" | "low" | null;
  correctiveAction: string;
  responsibleDepartmentId: number | null;
  targetCompletionDate: string | null;
}

export default function QaAssessment() {
  const [, params] = useRoute("/qa/visits/:id/assessment");
  const [, setLocation] = useLocation();
  const visitId = params?.id ? Number(params.id) : undefined;
  const perms = useQaPerms();
  const openEvidence = useOpenEvidence();

  const { data, isLoading, refetch } = trpc.qa.assessment.get.useQuery({ visitId: visitId! }, { enabled: !!visitId });
  const { data: depts = [] } = trpc.qa.meta.departments.useQuery();
  const saveRating = trpc.qa.assessment.saveRating.useMutation();
  const submit = trpc.qa.assessment.submit.useMutation();

  const [activeDomain, setActiveDomain] = useState(0);
  const [open, setOpen] = useState<Record<number, boolean>>({});
  const [state, setState] = useState<Record<number, AState>>({});
  const [liveScore, setLiveScore] = useState<{ overallScore: number; interpretationBand: string; domainScores: any[] } | null>(null);
  const init = useRef(false);

  if (data && !init.current) {
    init.current = true;
    const seed: Record<number, AState> = {};
    for (const q of data.questions) {
      const a = data.assessments.find((x: any) => x.questionId === q.id);
      seed[q.id] = {
        rating: (a?.rating as Rating) ?? null,
        evidenceReviewed: a?.evidenceReviewed ?? "",
        riskLevel: (a?.riskLevel as any) ?? null,
        correctiveAction: a?.correctiveAction ?? "",
        responsibleDepartmentId: a?.responsibleDepartmentId ?? null,
        targetCompletionDate: a?.targetCompletionDate ?? null,
      };
    }
    setState(seed);
    setLiveScore(data.score);
  }

  const responseByQ = useMemo(() => {
    const m: Record<number, any> = {};
    for (const r of data?.responses ?? []) m[r.questionId] = r;
    return m;
  }, [data?.responses]);
  const attByQ = useMemo(() => {
    const m: Record<number, any[]> = {};
    for (const a of data?.attachments ?? []) (m[a.questionId] ??= []).push(a);
    return m;
  }, [data?.attachments]);

  if (isLoading) return <div className="flex h-[50vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" style={{ color: QA.purple }} /></div>;
  if (!data) return <div className="p-6 text-sm text-gray-500">Visit not found.</div>;

  const locked = data.visit.status !== "under_review";
  const domains = data.domains;
  const domain = domains[activeDomain];

  function persist(questionId: number, patch: Partial<AState>) {
    const next = { ...(state[questionId] ?? { rating: null, evidenceReviewed: "", riskLevel: null, correctiveAction: "", responsibleDepartmentId: null, targetCompletionDate: null }), ...patch };
    setState((s) => ({ ...s, [questionId]: next }));
    if (!visitId) return;
    saveRating.mutate({
      visitId, questionId,
      rating: next.rating,
      evidenceReviewed: next.evidenceReviewed || null,
      riskLevel: next.riskLevel,
      correctiveAction: next.correctiveAction || null,
      responsibleDepartmentId: next.responsibleDepartmentId,
      targetCompletionDate: next.targetCompletionDate,
    }, { onSuccess: (res) => { if (res.score) setLiveScore(res.score); } });
  }

  const ratedCount = Object.values(state).filter((s) => s.rating).length;

  async function handleSubmit() {
    if (!visitId) return;
    try {
      await submit.mutateAsync({ visitId });
      toast.success("Assessment submitted");
      setLocation(`/qa/visits/${visitId}/report`);
    } catch (e: any) { toast.error(e?.message ?? "Submit failed"); }
  }

  const band = liveScore ? BAND_META[liveScore.interpretationBand] : null;

  return (
    <div style={{ background: QA.surface }} className="-mx-4 -my-4 min-h-screen md:-mx-6 md:-my-6">
      <div className="border-b px-6 py-4" style={{ background: QA.surface, borderColor: QA.border }}>
        <QaCrumb backTo={`/qa/visits/${visitId}`} items={[{ label: "Visits", to: "/qa/visits" }, { label: data.visit?.visitNumber ?? `Visit #${visitId}`, to: `/qa/visits/${visitId}`, mono: true }, { label: "Assessment" }]} />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-[20px] font-bold" style={{ color: QA.text }}>On-Site Assessment</h1>
            <p className="text-[13px]" style={{ color: QA.text2 }}>{data.visit.siteName ?? `Site #${data.visit.siteId}`} · {ratedCount}/{data.questions.length} rated</p>
          </div>
          {liveScore && (
            <div className="flex items-center gap-4 rounded-xl border px-4 py-2" style={{ background: QA.purpleXPale, borderColor: QA.border }}>
              <div className="text-right">
                <div className="text-[10px] uppercase" style={{ color: QA.text3 }}>Overall Score</div>
                <div className="text-[24px] font-bold leading-none" style={{ color: band?.color ?? QA.purple }}>{liveScore.overallScore}%</div>
              </div>
              {band && <div className="text-[11px] font-semibold" style={{ color: band.color }}>{band.label}</div>}
            </div>
          )}
        </div>
      </div>

      <div className="flex">
        <aside className="hidden w-[220px] shrink-0 border-r p-3 md:block" style={{ background: QA.surface, borderColor: QA.border }}>
          {domains.map((d: any, di: number) => {
            const ds = liveScore?.domainScores?.find((x: any) => x.domainId === d.id);
            return (
              <button key={d.id} onClick={() => setActiveDomain(di)} className="flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left"
                style={{ background: di === activeDomain ? QA.purpleXPale : "transparent", borderLeft: `3px solid ${di === activeDomain ? QA.purple : "transparent"}` }}>
                <span className="truncate text-[12px] font-semibold" style={{ color: di === activeDomain ? QA.purple : QA.text }}>{d.name}</span>
                <span className="text-[11px] font-bold" style={{ color: QA.text3 }}>{ds?.domainScore != null ? `${ds.domainScore}%` : "—"}</span>
              </button>
            );
          })}
        </aside>

        <main className="min-w-0 flex-1 px-6 pb-28 pt-5">
          {domain && (
            <>
              <div className="mb-4 text-[16px] font-bold" style={{ color: QA.purple }}>{domain.name}</div>
              {data.questions.filter((q: any) => q.domainId === domain.id).map((q: any) => (
                <AssessCard
                  key={q.id} q={q} st={state[q.id]} response={responseByQ[q.id]} attachments={attByQ[q.id] ?? []}
                  open={!!open[q.id]} locked={locked} depts={depts}
                  onToggle={() => setOpen((o) => ({ ...o, [q.id]: !o[q.id] }))}
                  onChange={(p) => persist(q.id, p)} onOpenEvidence={openEvidence}
                />
              ))}
            </>
          )}
        </main>
      </div>

      <div className="fixed bottom-0 right-0 z-40 flex items-center justify-between gap-3 border-t px-6 py-3" style={{ left: 0, background: QA.surface, borderColor: QA.border, boxShadow: "0 -4px 20px rgba(79,0,140,0.08)" }}>
        <span />
        <div className="flex gap-2">
          <button onClick={() => setActiveDomain((d) => Math.max(0, d - 1))} disabled={activeDomain === 0} className="rounded-lg border px-4 py-2 text-[12px] font-semibold disabled:opacity-40" style={{ borderColor: QA.border, color: QA.text }}>Previous</button>
          <button onClick={() => setActiveDomain((d) => Math.min(domains.length - 1, d + 1))} disabled={activeDomain >= domains.length - 1} className="rounded-lg px-4 py-2 text-[12px] font-semibold text-white disabled:opacity-40" style={{ background: QA.purple }}>Next Domain</button>
          {locked ? (
            <span className="rounded-lg px-4 py-2 text-[12px] font-semibold" style={{ background: QA.greenPale, color: QA.green }}>Assessment Submitted</span>
          ) : perms.assessmentSubmit ? (
            <button onClick={handleSubmit} disabled={submit.isPending} className="inline-flex items-center gap-1.5 rounded-lg px-5 py-2 text-[12px] font-semibold text-white disabled:opacity-50" style={{ background: QA.purple }}>
              {submit.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Submit Assessment
            </button>
          ) : <span />}
        </div>
      </div>
    </div>
  );
}

function AssessCard({ q, st, response, attachments, open, locked, depts, onToggle, onChange, onOpenEvidence }: {
  q: any; st?: AState; response: any; attachments: any[]; open: boolean; locked: boolean; depts: any[];
  onToggle: () => void; onChange: (p: Partial<AState>) => void; onOpenEvidence: (k: string) => void;
}) {
  const s = st ?? { rating: null, evidenceReviewed: "", riskLevel: null, correctiveAction: "", responsibleDepartmentId: null, targetCompletionDate: null };
  const meta = s.rating ? RATING_META[s.rating] : null;
  return (
    <div className="mb-2 overflow-hidden rounded-lg border bg-white" style={{ borderColor: QA.border, borderLeft: meta ? `4px solid ${meta.color}` : undefined }}>
      <div className="flex cursor-pointer items-start gap-3 p-3.5" onClick={onToggle}>
        <span className="mt-0.5 shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-bold" style={{ background: QA.purpleXPale, color: QA.purple }}>{q.questionNumber}</span>
        <span className="flex-1 text-[13px]" style={{ color: QA.text }}>{q.text}</span>
        {meta && <span className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ background: meta.bg, color: meta.color }}>{meta.short}</span>}
        <ChevronDown className="h-4 w-4 shrink-0" style={{ color: QA.text3, transform: open ? "rotate(180deg)" : undefined }} />
      </div>

      {open && (
        <div className="border-t px-4 pb-3.5 pt-3" style={{ borderColor: QA.border, background: QA.surface }}>
          {/* DC answer context */}
          <div className="mb-3 flex flex-wrap items-center gap-2 text-[12px]" style={{ color: QA.text2 }}>
            <span className="font-semibold" style={{ color: QA.text3 }}>DC answer:</span>
            <span>Availability: <b>{response?.availability ?? "—"}</b></span>
            <span>Practice: <b>{response?.beingInPractice ?? "—"}</b></span>
            {attachments.map((a) => (
              <button key={a.id} onClick={() => onOpenEvidence(a.fileKey)} className="inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] hover:underline" style={{ borderColor: QA.border }}>
                <Paperclip className="h-3 w-3" /> {a.fileName}
              </button>
            ))}
          </div>

          {/* Rating */}
          <div className="mb-1 text-[10px] font-bold uppercase tracking-wide" style={{ color: QA.text2 }}>Rating</div>
          <div className="mb-3 flex flex-wrap gap-1.5">
            {RATINGS.map((r) => {
              const m = RATING_META[r];
              const active = s.rating === r;
              return (
                <button key={r} disabled={locked} onClick={() => onChange({ rating: active ? null : r })}
                  className="rounded-md border-[1.5px] px-3 py-1.5 text-[12px] font-semibold disabled:opacity-60"
                  style={active ? { background: m.bg, color: m.color, borderColor: m.color } : { background: "#fff", color: QA.text2, borderColor: QA.border }}>
                  {m.label} <span className="opacity-60">({m.score})</span>
                </button>
              );
            })}
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <Labeled label="Evidence Reviewed">
              <textarea rows={2} disabled={locked} defaultValue={s.evidenceReviewed} onBlur={(e) => e.target.value !== s.evidenceReviewed && onChange({ evidenceReviewed: e.target.value })} className={inputCls} />
            </Labeled>
            <Labeled label="Corrective Action">
              <textarea rows={2} disabled={locked} defaultValue={s.correctiveAction} onBlur={(e) => e.target.value !== s.correctiveAction && onChange({ correctiveAction: e.target.value })} className={inputCls} />
            </Labeled>
            <Labeled label="Risk Level">
              <div className="flex gap-1.5">
                {RISKS.map((r) => {
                  const color = r === "high" ? QA.red : r === "medium" ? QA.amber : QA.green;
                  const active = s.riskLevel === r;
                  return <button key={r} disabled={locked} onClick={() => onChange({ riskLevel: active ? null : r })} className="flex-1 rounded-md border-[1.5px] px-2 py-1.5 text-[12px] font-semibold capitalize disabled:opacity-60" style={active ? { background: `${color}1a`, color, borderColor: color } : { background: "#fff", color: QA.text2, borderColor: QA.border }}>{r}</button>;
                })}
              </div>
            </Labeled>
            <Labeled label="Responsible Department">
              <select disabled={locked} value={s.responsibleDepartmentId ?? ""} onChange={(e) => onChange({ responsibleDepartmentId: e.target.value ? Number(e.target.value) : null })} className={inputCls}>
                <option value="">—</option>
                {depts.map((d: any) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </Labeled>
            <Labeled label="Target Completion Date">
              <input type="date" disabled={locked} value={s.targetCompletionDate ?? ""} onChange={(e) => onChange({ targetCompletionDate: e.target.value || null })} className={inputCls} />
            </Labeled>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full resize-none rounded-md border-[1.5px] border-[#E5E0EF] bg-white px-2.5 py-2 text-[12px] outline-none";

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: QA.text2 }}>{label}</div>
      {children}
    </div>
  );
}
